import React from "react";
import { Link } from "react-router-dom";
import useSEO from "@/hooks/useSEO";

const CONTACT_EMAIL = "legal@proofmark.app";
const LAST_UPDATED = "2 July 2026";

export default function TermsOfService() {
  useSEO({
    title: "Terms of Service — ProofMark",
    description:
      "The terms governing use of ProofMark — acceptable use, customer data ownership, IP, warranties, liability, and governing law.",
    path: "/terms-of-service",
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/80 px-6 py-4 flex items-center gap-3">
        <Link to="/" className="font-serif-display text-lg text-foreground hover:opacity-80 transition-opacity">
          ProofMark
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="font-serif-display text-3xl mb-2">Terms of Service</h1>
        <p className="text-sm text-muted-foreground mb-12">Last updated: {LAST_UPDATED}</p>

        <section className="space-y-10 text-sm leading-relaxed text-muted-foreground">

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">1. Acceptance</h2>
            <p>
              By accessing or using ProofMark (the &ldquo;Service&rdquo;), you agree to be bound by these Terms of Service.
              If you are using the Service on behalf of an organization, you represent that you have the authority to bind that organization to these terms.
              If you do not agree, do not use the Service.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">2. Description of service</h2>
            <p>
              ProofMark is an enterprise AI knowledge governance platform that provides a governed review and evidence system
              for managing what operational knowledge enters AI systems, on what basis, and with what revocation path.
              The Service includes a web application, API, and associated documentation.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">3. Accounts</h2>
            <p className="mb-3">You are responsible for:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activity that occurs under your account</li>
              <li>Notifying us immediately of any unauthorized access</li>
            </ul>
            <p className="mt-3">
              You may not share accounts, transfer accounts to third parties, or create accounts for automated access without our prior written consent.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">4. Acceptable use</h2>
            <p className="mb-3">You agree not to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Use the Service for any unlawful purpose or in violation of applicable regulations</li>
              <li>Attempt to gain unauthorized access to any part of the Service or its infrastructure</li>
              <li>Reverse-engineer, decompile, or disassemble any part of the Service</li>
              <li>Use the Service to store, transmit, or process content that violates third-party intellectual property rights</li>
              <li>Introduce malicious code, conduct denial-of-service attacks, or interfere with Service integrity</li>
              <li>Resell, sublicense, or otherwise commercialize access to the Service without our written authorization</li>
            </ul>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">5. Customer data</h2>
            <p>
              You retain all rights to the data you submit to the Service (&ldquo;Customer Data&rdquo;). We process Customer Data solely to provide the Service.
              We do not use Customer Data to train AI models or share it with third parties except as described in our Privacy Policy.
              You are responsible for ensuring that your use of the Service with respect to Customer Data complies with applicable laws,
              including obtaining any necessary consents from individuals whose data may be processed.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">6. Intellectual property</h2>
            <p>
              The Service, including its software, design, documentation, and trademarks, is owned by us and protected by intellectual property laws.
              These terms do not grant you any rights to our intellectual property except the limited right to use the Service as described herein.
              You grant us a limited license to use Customer Data solely to operate and improve the Service.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">7. AI-generated content</h2>
            <p>
              The Service uses AI models (including Claude by Anthropic) to assist with artifact drafting and refinement.
              AI-generated suggestions are provided for review purposes only. You are solely responsible for reviewing,
              approving, and taking accountability for any content published through the Service.
              We make no warranties about the accuracy or completeness of AI-generated content.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">8. Service availability</h2>
            <p>
              We aim to provide reliable service but do not guarantee uninterrupted availability.
              We may perform maintenance, updates, or emergency interventions that temporarily affect access.
              We are not liable for any losses arising from Service interruptions.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">9. Limitation of liability</h2>
            <p>
              To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential,
              or punitive damages, or any loss of profits, revenue, data, or business opportunities arising from your use of the Service.
              Our total liability for any claim arising from these terms or your use of the Service shall not exceed the amounts
              you paid to us in the 12 months preceding the claim, or £100 if you have not made any payments.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">10. Disclaimer of warranties</h2>
            <p>
              The Service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without warranties of any kind, express or implied,
              including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.
              We do not warrant that the Service will meet your specific requirements or that it will be error-free.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">11. Termination</h2>
            <p>
              We may suspend or terminate your access to the Service at any time if you violate these terms or if we reasonably
              believe your use poses a security risk to us or other users. You may stop using the Service at any time.
              Upon termination, your right to access the Service ceases. Customer Data will be available for export for 30 days following termination.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">12. Changes to these terms</h2>
            <p>
              We may update these terms from time to time. Material changes will be communicated via email at least 14 days before they take effect.
              Continued use of the Service after the effective date constitutes acceptance of the revised terms.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">13. Governing law</h2>
            <p>
              These terms are governed by the laws of England and Wales.
              Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts of England and Wales,
              except where mandatory local consumer protection laws apply.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">14. Contact</h2>
            <p>
              Questions about these terms: <a href={`mailto:${CONTACT_EMAIL}`} className="text-foreground underline underline-offset-2">{CONTACT_EMAIL}</a>.
            </p>
          </div>

        </section>

        <div className="mt-16 pt-8 border-t border-border/80 flex gap-6 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground transition-colors">← Back to home</Link>
          <Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
        </div>
      </main>
    </div>
  );
}
